import { useMemo, useState } from "react";
import {
  buildMockDocumentosImpresion,
  mockCustomers,
  mockEvent,
  mockPaymentMethods,
  mockProducts,
  mockRecentSales,
  mockVouchers,
} from "../data/ventaEventoMock";
import {
  calculateSaleTotals,
  getCustomerDisplayName,
} from "../utils";

const createConsumer = (dni = "") => ({
  id: null,
  dni,
  firstName: "",
  lastName: "",
  communityRole: "",
  isCommunityMember: false,
});

export default function useVentaEventoDemo() {
  const [cart, setCart] = useState({});
  const [selectionOrder, setSelectionOrder] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [paymentCode, setPaymentCode] = useState("qr");
  const [voucherCode, setVoucherCode] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [recentSales, setRecentSales] = useState(mockRecentSales);
  const products = useMemo(
    () => mockProducts.map((product) => ({ ...product, stockTeorico: 20 })),
    []
  );

  const cartItems = useMemo(
    () =>
      products
        .filter((product) => cart[product.id])
        .map((product) => ({ ...product, quantity: cart[product.id] })),
    [cart, products]
  );

  const voucher = mockVouchers.find((item) => item.code === voucherCode) || null;
  const totals = useMemo(
    () => calculateSaleTotals({ cartItems, customer, voucher, selectionOrder }),
    [cartItems, customer, selectionOrder, voucher]
  );

  const addProduct = (productId) => {
    setCart((current) => ({ ...current, [productId]: (current[productId] || 0) + 1 }));
    setSelectionOrder((current) => [...current, productId]);
  };

  const changeQuantity = (productId, amount) => {
    if (amount > 0) {
      addProduct(productId);
      return;
    }

    setCart((current) => {
      const nextQuantity = Math.max((current[productId] || 0) + amount, 0);
      const next = { ...current };
      if (nextQuantity === 0) delete next[productId];
      else next[productId] = nextQuantity;
      return next;
    });
    setSelectionOrder((current) => {
      const lastIndex = current.lastIndexOf(productId);
      return lastIndex < 0
        ? current
        : [...current.slice(0, lastIndex), ...current.slice(lastIndex + 1)];
    });
  };

  const identifyCustomer = (rawDni) => {
    const dni = String(rawDni || "").replace(/\D/g, "");
    const knownCustomer = mockCustomers.find((item) => item.dni === dni);
    const nextCustomer = knownCustomer || createConsumer(dni);
    setCustomer(nextCustomer);
    return nextCustomer;
  };

  const useAnonymousConsumer = () => setCustomer(createConsumer());

  const resetCustomer = () => setCustomer(null);

  const selectVoucher = (code) => {
    if (!code) {
      setVoucherCode("");
      return { applied: true };
    }
    const nextVoucher = mockVouchers.find((item) => item.code === code);
    if (!nextVoucher) return { applied: false, message: "No encontramos el voucher seleccionado." };

    if (nextVoucher.type === "PRODUCTO") {
      const productId = Number(nextVoucher.productId);
      const missingUnits = Math.max(Number(nextVoucher.units || 0) - Number(cart[productId] || 0), 0);
      if (missingUnits > 0) {
        setCart((current) => ({
          ...current,
          [productId]: Number(current[productId] || 0) + missingUnits,
        }));
        setSelectionOrder((current) => [
          ...current,
          ...Array.from({ length: missingUnits }, () => productId),
        ]);
      }
    }

    setVoucherCode(code);
    return { applied: true };
  };

  const clearSale = () => {
    setCart({});
    setSelectionOrder([]);
    setCustomer(null);
    setPaymentCode("qr");
    setVoucherCode("");
    setObservaciones("");
  };

  const completeSale = () => {
    const method = mockPaymentMethods.find((item) => item.code === paymentCode);
    const numero = 87 + recentSales.length - mockRecentSales.length;
    const sale = {
      id: `EV-${String(numero).padStart(4, "0")}`,
      time: new Date().toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      customer: getCustomerDisplayName(customer),
      items: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      payment: totals.amountDue <= 0.01 ? "No requerido" : method?.label || "Sin definir",
      benefit: voucher?.name || null,
      total: totals.total,
      status: "Aprobado",
      reutilizada: false,
      // La demo imprime de verdad: es la forma de probar cola e impresora sin
      // backend. No fabrica factura.
      documentosImpresion: buildMockDocumentosImpresion({
        saleId: numero,
        cartItems,
        totals,
        voucher,
        seller: mockEvent.seller,
        observaciones: observaciones.trim() || null,
      }),
    };
    setRecentSales((current) => [sale, ...current]);
    return sale;
  };

  return {
    products,
    customers: mockCustomers,
    paymentMethods: mockPaymentMethods,
    vouchers: mockVouchers,
    cart,
    cartItems,
    customer,
    customerCandidates: [],
    customerLoading: false,
    customerError: "",
    paymentCode,
    voucherCode,
    voucher,
    observaciones,
    totals,
    recentSales,
    addProduct,
    changeQuantity,
    identifyCustomer,
    selectCustomerCandidate: () => null,
    resetCustomer,
    useAnonymousConsumer,
    setPaymentCode,
    setObservaciones,
    selectVoucher,
    clearSale,
    completeSale,
  };
}
